import logging from 'utils/logging';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
  SUBMISSION_STATE_WAGER,
} from '../../gamestate/constants';
import { advanceState } from '../../gamestate/GameState';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.AllSubmissionsReceived');
log.setLevel('debug');

const AUTO_ADVANCE_DELAY_MS = 200;

// Track which questions have been auto-advanced to prevent duplicate triggers
const questionsAutoAdvanced = new Set();

export const resetAllSubmissionsReceivedCache = () => {
  questionsAutoAdvanced.clear();
};
registerCacheReset('round', resetAllSubmissionsReceivedCache);

/**
 * Moment that triggers when all required players have submitted answers,
 * automatically advancing from QUESTION_ANSWERING to QUESTION_JUDGING.
 * No narration is generated — this is a pure state-advance moment.
 */
export const AllSubmissionsReceivedMoment = {
  id: 'ALL_SUBMISSIONS_RECEIVED',

  shouldTrigger: ({ currentState, currentPlayers, questions, submissions }) => {
    // Only trigger during the answering phase
    if (currentState?.currentState !== GAMESTATE_ROUND) {
      return false;
    }
    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING) {
      return false;
    }

    // Final round always uses the full 30s clock — players' answers stay
    // hidden until everyone has had the same chance to lock in. The timer-
    // expired path is handled by FinalAnsweringTimeoutMoment.
    if (currentState?.round === GAMESTATE_ROUND_FINAL) {
      return false;
    }

    const questionId = currentState?.questionId;
    if (!questionId || !currentPlayers || currentPlayers.length === 0) {
      return false;
    }

    // Prevent duplicate triggers for the same question
    if (questionsAutoAdvanced.has(questionId)) {
      return false;
    }

    // Determine if this is a daily double (only 1 player needs to answer)
    const question = questions?.find(q => q.id === questionId);
    const isDailyDouble = question?.dailyDouble === true;
    const expectedCount = isDailyDouble ? 1 : currentPlayers.length;

    // Count submissions for this question that are not just wagers
    const answeredCount = (submissions || []).filter(
      (playerSub) => playerSub.submission && playerSub.submission.status !== SUBMISSION_STATE_WAGER
    ).length;

    log.debug(`AllSubmissionsReceived: isDailyDouble=${isDailyDouble}, answeredCount=${answeredCount}, expectedCount=${expectedCount}, questionId=${questionId}`);

    if (answeredCount >= expectedCount) {
      // Mark this question as auto-advanced to prevent duplicate triggers
      questionsAutoAdvanced.add(questionId);
      log.info(`All ${answeredCount}/${expectedCount} submissions received for question ${questionId}. Scheduling auto-advance.`);

      return {
        shouldTriggerAction: true,
        shouldGenerateNarration: false,
        triggerAction: AllSubmissionsReceivedMoment.triggerAction,
      };
    }

    return false;
  },

  /**
   * Advances the game state from QUESTION_ANSWERING to QUESTION_JUDGING
   * after a short delay for UX smoothness.
   */
  triggerAction: async (gameObject, players, dispatch) => {
    try {
      log.debug('All submissions received — waiting before advancing to judging');
      await new Promise(resolve => setTimeout(resolve, AUTO_ADVANCE_DELAY_MS));
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error auto-advancing after all submissions received:', error);
    }
  },
};

const api = {
  trigger: AllSubmissionsReceivedMoment,
};

export default api;
